import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { ChevronRight, BookOpen, Users, Award, TrendingUp, Play, CheckCircle, Star, ArrowRight, Globe, Shield, Zap, Monitor, MessageCircle, BarChart3, CheckCircleIcon, Target, Heart, Sparkles, GraduationCap, Palette, Music, Dumbbell, Languages, Lightbulb, Calendar, Video, Clock } from 'lucide-react';

const LandingPage = () => {
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % 3);
    }, 5000);

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const courses = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Personality Development",
      description: "Boost your confidence, communication, and leadership skills",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80",
      color: "bg-pastel-rose",
      accentColor: "#FFB5BA"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "English Communication",
      description: "Master grammar, vocabulary, and spoken English fluency",
      image: "https://images.unsplash.com/photo-1543165365-07232ed12fad?w=800&q=80",
      color: "bg-pastel-blue",
      accentColor: "#A5C9E1"
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "Computer Skills",
      description: "Learn MS Office, coding, and essential digital tools",
      image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&q=80",
      color: "bg-pastel-purple",
      accentColor: "#C5B4E3"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Creative Arts",
      description: "Express creativity through drawing, painting, and design",
      image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=800&q=80",
      color: "bg-pastel-peach",
      accentColor: "#FFDAB9"
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Music & Rhythm",
      description: "Learn instruments, vocal training, and music theory",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&q=80",
      color: "bg-pastel-mint",
      accentColor: "#B5E7B9"
    },
    {
      icon: <Dumbbell className="w-6 h-6" />,
      title: "Physical Education",
      description: "Enhance fitness, sports skills, and overall wellness",
      image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80",
      color: "bg-pastel-yellow",
      accentColor: "#FFF5BA"
    }
  ];

  const features = [
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Seamless Course Management",
      description: "Admins, colleges, and SMEs can create and manage courses with ease",
      color: "bg-pastel-lavender"
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: "Interactive Learning",
      description: "Engage with video lectures, discussions, and quizzes in multiple languages",
      color: "bg-pastel-mint"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Smart Analytics",
      description: "Real-time dashboards to track progress and performance",
      color: "bg-pastel-blue"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Verified Certification",
      description: "Digital certificates with authenticity checks and official signatures",
      color: "bg-pastel-peach"
    }
  ];

  const stats = [
    { value: "50,000+", label: "Active Learners", icon: <Users className="w-8 h-8" /> },
    { value: "1000+", label: "Expert Instructors", icon: <GraduationCap className="w-8 h-8" /> },
    { value: "500+", label: "Courses Available", icon: <BookOpen className="w-8 h-8" /> },
    { value: "98%", label: "Success Rate", icon: <TrendingUp className="w-8 h-8" /> }
  ];

  const testimonials = [
    {
      name: "Arjun Mehta",
      role: "Software Engineer",
      content: "GYAN SATHI transformed my career completely. The coding bootcamp was incredible, and the community support was unmatched.",
      image: "https://ui-avatars.com/api/?name=Arjun+Mehta&background=A5C9E1&color=1A3A5C"
    },
    {
      name: "Sneha Singh",
      role: "Marketing Manager",
      content: "The communication skills course boosted my confidence tremendously. Now I lead presentations with complete ease!",
      image: "https://ui-avatars.com/api/?name=Sneha+Singh&background=FFB5BA&color=5C1A1A"
    },
    {
      name: "Rahul Verma",
      role: "Entrepreneur",
      content: "From yoga to business skills, GYAN SATHI offers everything. It's truly my one-stop destination for growth.",
      image: "https://ui-avatars.com/api/?name=Rahul+Verma&background=FFF5BA&color=5C4A1A"
    }
  ];

  const benefits = [
    { icon: <Clock className="w-5 h-5" />, text: "Learn at your own pace" },
    { icon: <Globe className="w-5 h-5" />, text: "Access from anywhere" },
    { icon: <Shield className="w-5 h-5" />, text: "Certified instructors" },
    { icon: <Lightbulb className="w-5 h-5" />, text: "Industry-relevant skills" }
  ];

  return (
    <div className="min-h-screen overflow-hidden bg-pastel-cream">
      {/* Navigation */}
      {/* <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-xl shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-pastel-blue-dark rounded-2xl flex items-center justify-center shadow-soft">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold text-pastel-navy">GYAN SATHI</span>
                <p className="text-xs text-gray-600 -mt-0.5">Smart Learning Platform</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-10">
              <a href="#courses" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Courses</a>
              <a href="#features" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Features</a>
              <a href="#about" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">About</a>
              <a href="#testimonials" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Success Stories</a>
              <Link href="/contact-us" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Contact Us</Link>
              <Link href="/privacy-policy" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Privacy Policy</Link>
              <Link href="/terms-and-conditions" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Terms</Link>
            </div>
            <button
              onClick={() => router.push('/auth/login')}
              className="px-7 py-3 bg-pastel-blue-dark text-white rounded-full font-semibold hover:shadow-soft-xl transform hover:-translate-y-0.5 transition-all duration-300 text-sm"
            >
              Login
            </button>
          </div>
        </div>
      </nav> */}

      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-xl shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/logo-icon.png" 
                alt="Gyan Sathi Logo" 
                className="h-12 w-auto" 
              />
              <div>
                <span className="text-2xl font-bold text-pastel-navy">GYAN SATHI</span>
                <p className="text-xs text-gray-600 -mt-0.5">Smart Learning Platform</p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-10">
              <a href="#courses" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Courses</a>
              <a href="#features" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Features</a>
              <a href="#about" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">About</a>
              <a href="#testimonials" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Success Stories</a>
              <Link href="/contact-us" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Contact Us</Link>
              <Link href="/privacy-policy" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Privacy Policy</Link>
              <Link href="/terms-and-conditions" className="text-gray-700 hover:text-pastel-blue-dark transition-colors font-medium text-sm">Terms</Link>
            </div>
            <button
              onClick={() => router.push('/auth/login')}
              className="px-7 py-3 bg-pastel-blue-dark text-white rounded-full font-semibold hover:shadow-soft-xl transform hover:-translate-y-0.5 transition-all duration-300 text-sm"
            >
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Enhanced Hero Section */}
      <section className="relative pt-20 min-h-screen flex items-center overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-pastel-cream"></div>
          
          {/* Floating Shapes */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-pastel-rose/30 rounded-full blur-3xl animate-float"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-pastel-blue/30 rounded-full blur-3xl animate-float-delayed"></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-pastel-lavender/30 rounded-full blur-3xl animate-float-slow"></div>
          
          {/* Decorative Elements */}
          <div className="absolute top-32 right-1/4 w-20 h-20 border-4 border-pastel-peach/40 rounded-2xl rotate-12 animate-spin-slow"></div>
          <div className="absolute bottom-32 left-1/4 w-16 h-16 border-4 border-pastel-mint/40 rounded-full animate-pulse-soft"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className={`space-y-8 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
              {/* Badge */}
              <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm border-2 border-pastel-yellow rounded-full px-5 py-2.5 shadow-soft">
                <Sparkles className="w-4 h-4 text-pastel-peach-dark" />
                <span className="text-sm font-semibold text-pastel-navy">Transforming Lives Since 2020</span>
              </div>
              
              {/* Main Headline */}
              <div className="space-y-4">
                <h1 className="text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-pastel-navy">Unlock Your</span>
                  <br />
                  <span className="text-pastel-blue-dark">Potential</span>
                </h1>
                <div className="flex items-center space-x-3">
                  <div className="h-1.5 w-20 bg-pastel-rose rounded-full"></div>
                  <div className="h-1.5 w-12 bg-pastel-yellow rounded-full"></div>
                  <div className="h-1.5 w-8 bg-pastel-mint rounded-full"></div>
                </div>
              </div>
              
              {/* Subtitle */}
              <p className="text-xl text-gray-700 leading-relaxed max-w-xl">
                Master new skills with <span className="font-semibold text-pastel-navy">500+ expert-led courses</span> in coding, 
                communication, creative arts, and personal development. Learn at your pace, grow at your speed.
              </p>
              
              {/* Benefits Grid */}
              <div className="grid grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2 bg-white/60 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-soft">
                    <div className="text-pastel-blue-dark">{benefit.icon}</div>
                    <span className="text-sm font-medium text-gray-700">{benefit.text}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => router.push('/auth/login')}
                  className="group px-8 py-4 bg-pastel-blue-dark text-white rounded-full hover:shadow-soft-2xl transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-semibold"
                >
                  <span>Start Learning Free</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="group px-8 py-4 bg-white border-2 border-pastel-blue-dark text-pastel-navy rounded-full hover:bg-pastel-blue/10 transition-all duration-300 flex items-center justify-center space-x-2 text-lg font-semibold shadow-soft">
                  <Play className="w-5 h-5" />
                  <span>Watch Demo</span>
                </button>
              </div>
              
              {/* Social Proof */}
              <div className="flex items-center gap-8 pt-4">
                <div className="flex -space-x-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-12 h-12 rounded-full border-3 border-white shadow-soft overflow-hidden">
                      <img
                        src={`https://ui-avatars.com/api/?name=User${i}&background=${i % 3 === 0 ? 'A5C9E1' : i % 3 === 1 ? 'FFB5BA' : 'B5E7B9'}&color=fff`}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex text-pastel-yellow-dark mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-700 font-semibold">50,000+ happy learners</p>
                </div>
              </div>
            </div>
            
            {/* Right Side - Clean Images */}
            <div className={`relative ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
              <div className="relative">
                {/* Main Course Grid with Clear Images */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-soft-2xl p-6 border border-gray-100">
                  <div className="grid grid-cols-2 gap-5">
                    {courses.slice(0, 4).map((course, index) => (
                      <div
                        key={index}
                        className="relative group overflow-hidden rounded-2xl shadow-soft hover:shadow-soft-xl cursor-pointer transform hover:scale-105 transition-all duration-300"
                        onMouseEnter={() => setHoveredCourse(index)}
                        onMouseLeave={() => setHoveredCourse(null)}
                      >
                        {/* Clear Image - NO OVERLAYS */}
                        <div className="relative h-48">
                          <img 
                            src={course.image} 
                            alt={course.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Content Card Below Image */}
                        <div className={`${course.color} p-4`}>
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft text-pastel-navy flex-shrink-0">
                              {course.icon}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-pastel-navy font-bold text-sm leading-tight mb-1">{course.title}</h3>
                              <p className="text-xs text-gray-700 line-clamp-2">{course.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* View All Courses Link */}
                  <button className="mt-6 w-full py-3 bg-pastel-blue-dark/10 hover:bg-pastel-blue-dark/20 text-pastel-navy font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2">
                    <span>View All Courses</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Floating Stats Cards */}
                <div className="absolute -top-6 -left-6 bg-white rounded-2xl shadow-soft-xl p-4 animate-float border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pastel-mint rounded-xl flex items-center justify-center">
                      <Users className="w-5 h-5 text-pastel-navy" />
                    </div>
                    <div>
                      <p className="font-bold text-lg text-pastel-navy">50K+</p>
                      <p className="text-xs text-gray-600">Students</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-soft-xl p-4 animate-float-delayed border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pastel-yellow rounded-xl flex items-center justify-center">
                      <Trophy className="w-5 h-5 text-pastel-navy" />
                    </div>
                    <div>
                      <p className="font-bold text-lg text-pastel-navy">98%</p>
                      <p className="text-xs text-gray-600">Success Rate</p>
                    </div>
                  </div>
                </div>
                
                <div className="absolute top-1/2 -left-8 bg-white rounded-2xl shadow-soft-xl p-4 animate-float-slow border border-gray-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-pastel-rose rounded-xl flex items-center justify-center">
                      <Star className="w-5 h-5 text-pastel-navy fill-current" />
                    </div>
                    <div>
                      <p className="font-bold text-lg text-pastel-navy">4.9</p>
                      <p className="text-xs text-gray-600">Rating</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-pastel-blue/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="w-16 h-16 bg-white shadow-soft rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform text-pastel-blue-dark">
                  {stat.icon}
                </div>
                <div className="text-4xl font-bold text-pastel-navy mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section - Clean Images */}
      <section id="courses" className="py-20 bg-pastel-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-pastel-blue/20 rounded-full px-5 py-2 mb-4">
              <span className="text-sm font-semibold text-pastel-navy">Popular Programs</span>
            </div>
            <h2 className="text-5xl font-bold mb-4 text-pastel-navy">
              Explore Our Courses
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Learn at your own pace with expert-designed courses tailored for students, professionals, and organizations
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <div
                key={index}
                className="group relative overflow-hidden bg-white rounded-3xl shadow-soft hover:shadow-soft-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100"
              >
                {/* Clear Image Section - NO OVERLAYS */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                
                {/* Content Section with Colored Background */}
                <div className={`${course.color} p-6`}>
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-soft text-pastel-navy flex-shrink-0">
                      {course.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-pastel-navy mb-2">{course.title}</h3>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">{course.description}</p>
                  <button className="flex items-center text-pastel-blue-dark font-semibold group-hover:gap-2 transition-all text-sm">
                    <span>Explore Course</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-20 w-96 h-96 bg-pastel-lavender/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-pastel-mint/20 rounded-full blur-3xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-pastel-rose/20 rounded-full px-5 py-2 mb-4">
              <span className="text-sm font-semibold text-pastel-navy">Platform Features</span>
            </div>
            <h2 className="text-5xl font-bold mb-4 text-pastel-navy">
              Why Choose GYAN SATHI?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              A multilingual, modern Learning Management System making education more accessible, interactive, and effective
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group text-center bg-white rounded-3xl p-8 shadow-soft hover:shadow-soft-xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-20 h-20 ${feature.color} rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-soft group-hover:scale-110 transition-all duration-300 text-pastel-navy`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-pastel-navy mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-pastel-blue-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
            <span className="text-sm font-semibold">Our Story</span>
          </div>
          <h2 className="text-5xl font-bold mb-8">Empowering Minds, Transforming Lives</h2>
          <p className="text-xl mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
            Born from a vision of universal learning, GYAN SATHI was founded in 2020 with a belief that quality education should know no boundaries. 
            What started as a small initiative to democratize coding education has evolved into a comprehensive platform offering everything from 
            technical skills to wellness practices.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-14 h-14 bg-pastel-yellow rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-7 h-7 text-pastel-navy" />
              </div>
              <h3 className="text-xl font-bold mb-3">Our Vision</h3>
              <p className="text-white/80 leading-relaxed">Quality education accessible to everyone, regardless of background</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-14 h-14 bg-pastel-rose rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-7 h-7 text-pastel-navy" />
              </div>
              <h3 className="text-xl font-bold mb-3">Our Mission</h3>
              <p className="text-white/80 leading-relaxed">Empowering through comprehensive learning experiences</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:bg-white/20 transition-colors">
              <div className="w-14 h-14 bg-pastel-mint rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-pastel-navy" />
              </div>
              <h3 className="text-xl font-bold mb-3">Our Values</h3>
              <p className="text-white/80 leading-relaxed">Excellence, innovation, community, and accessibility</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-pastel-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-pastel-yellow/30 rounded-full px-5 py-2 mb-4">
              <span className="text-sm font-semibold text-pastel-navy">Success Stories</span>
            </div>
            <h2 className="text-5xl font-bold mb-4 text-pastel-navy">
              What Our Learners Say
            </h2>
            <p className="text-xl text-gray-700">Real stories from real people who transformed their lives</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl shadow-soft-2xl p-10 md:p-14 relative border border-gray-100">
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                <div className="bg-pastel-yellow rounded-full p-4 shadow-soft">
                  <Award className="w-8 h-8 text-pastel-navy" />
                </div>
              </div>
              
              <div className="flex items-center justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-pastel-yellow-dark fill-current" />
                ))}
              </div>
              
              <p className="text-2xl text-gray-700 text-center mb-10 italic leading-relaxed">
                "{testimonials[activeTestimonial].content}"
              </p>
              
              <div className="flex items-center justify-center space-x-4">
                <img 
                  src={testimonials[activeTestimonial].image} 
                  alt={testimonials[activeTestimonial].name}
                  className="w-16 h-16 rounded-full border-4 border-pastel-blue/30 shadow-soft"
                />
                <div className="text-left">
                  <p className="font-bold text-pastel-navy text-lg">{testimonials[activeTestimonial].name}</p>
                  <p className="text-gray-600">{testimonials[activeTestimonial].role}</p>
                </div>
              </div>
              
              <div className="flex justify-center space-x-2 mt-10">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`h-2.5 rounded-full transition-all ${
                      index === activeTestimonial ? 'bg-pastel-blue-dark w-10' : 'bg-gray-300 w-2.5'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-pastel-blue-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(30deg, white 12%, transparent 12.5%, transparent 87%, white 87.5%, white), linear-gradient(150deg, white 12%, transparent 12.5%, transparent 87%, white 87.5%, white), linear-gradient(30deg, white 12%, transparent 12.5%, transparent 87%, white 87.5%, white), linear-gradient(150deg, white 12%, transparent 12.5%, transparent 87%, white 87.5%, white)',
            backgroundSize: '80px 140px',
            backgroundPosition: '0 0, 0 0, 40px 70px, 40px 70px'
          }}></div>
        </div>
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
            <span className="text-sm font-semibold text-white">Join Today</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6">
            Ready to Transform Your Future?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Join thousands of learners who are already building their dream careers with GYAN SATHI
          </p>
          <button
            onClick={() => router.push('/auth/login')}
            className="group px-12 py-4 bg-white text-pastel-blue-dark rounded-full hover:shadow-soft-2xl transform hover:-translate-y-1 transition-all duration-300 text-lg font-bold inline-flex items-center space-x-3"
          >
            <span>Get Started Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-6 text-white/70 text-sm">No credit card required • Start learning in 2 minutes</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-pastel-navy text-white py-16 px-4 sm:px-6 lg:px-8">
  <div className="max-w-7xl mx-auto">
    <div className="grid md:grid-cols-4 gap-10 mb-12">
      <div>
        <div className="flex items-center space-x-3 mb-5">
          <img
            src="/assets/logo-icon.png"
            alt="Gyan Sathi Logo"
            className="h-12 w-auto"
          />
          <span className="text-2xl font-bold">GYAN SATHI</span>
        </div>
        <p className="text-white/70 leading-relaxed">Empowering learners worldwide with quality education since 2020.</p>
      </div>
      <div>
        <h3 className="font-bold mb-5 text-pastel-yellow text-lg">Quick Links</h3>
        <ul className="space-y-3">
          <li><a href="#courses" className="text-white/70 hover:text-white transition-colors">Courses</a></li>
          <li><a href="#about" className="text-white/70 hover:text-white transition-colors">About Us</a></li>
          <li><Link href="/contact-us" className="text-white/70 hover:text-white transition-colors">Contact</Link></li>
          <li><Link href="/privacy-policy" className="text-white/70 hover:text-white transition-colors">Privacy Policy</Link></li>
          <li><Link href="/terms-and-conditions" className="text-white/70 hover:text-white transition-colors">Terms &amp; Conditions</Link></li>
        </ul>
      </div>
      <div>
        <h3 className="font-bold mb-5 text-pastel-yellow text-lg">Resources</h3>
        <ul className="space-y-3">
          <li><a href="#" className="text-white/70 hover:text-white transition-colors">Blog</a></li>
          <li><a href="#" className="text-white/70 hover:text-white transition-colors">Help Center</a></li>
          <li><a href="#" className="text-white/70 hover:text-white transition-colors">Community</a></li>
          <li><a href="#" className="text-white/70 hover:text-white transition-colors">Events</a></li>
        </ul>
      </div>
      <div>
        <h3 className="font-bold mb-5 text-pastel-yellow text-lg">Contact Info</h3>
        <ul className="space-y-3 text-white/70">
          <li>
            <a href="mailto:info@gyansathi.com" className="hover:text-white transition-colors">
              info@gyansathi.com
            </a>
          </li>
          <li>+91 8791031516</li>
          <li>
            <a
              href="https://www.google.com/maps?q=28.946156,77.754158"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
            >
              SF 66A, Somdutt City, A Pocket, Meerut
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 pt-8 text-center">
      <p className="text-white/50">
        &copy; 2025 GYAN SATHI entity of Shree Sai Enterprise. All rights reserved.
      </p>
      <p className="text-white/40 mt-2 text-sm">
        Developed by{' '}
        <a 
          href="https://socialflynetworks.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-pastel-yellow hover:text-white transition-colors underline underline-offset-2"
        >
          Socialfly Networks
        </a>
      </p>
    </div>
  </div>
</footer>

      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes float-delayed {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse-soft {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.6;
            transform: scale(1.05);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 7s ease-in-out infinite;
          animation-delay: 1s;
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
          animation-delay: 2s;
        }

        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }

        .animate-pulse-soft {
          animation: pulse-soft 3s ease-in-out infinite;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

// Trophy icon component
const Trophy = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94A5.01 5.01 0 0011 15.9V19H7v2h10v-2h-4v-3.1a5.01 5.01 0 003.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z"/>
  </svg>
);

export default LandingPage;
